'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
	Activity,
	Banknote,
	Bell,
	Bot,
	Building,
	Calendar,
	CalendarCheck,
	CalendarClock,
	CalendarDays,
	CheckSquare,
	ChevronDown,
	ClipboardCheck,
	Clock,
	CreditCard,
	FileText,
	GitBranch,
	GraduationCap,
	Handshake,
	Hash,
	HeartHandshake,
	Home,
	IdCard,
	LayoutDashboard,
	LifeBuoy,
	LogOut,
	Megaphone,
	Menu,
	Receipt,
	Settings2,
	Shield,
	ShieldCheck,
	SlidersHorizontal,
	Store,
	Target,
	UserPlus,
	Users,
	Wallet,
	X
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api } from '$lib/api/client';
import type { ServerTimeResponse } from '$lib/api/types';
import { useAuth } from '$lib/app/auth-context';
import { useCompany } from '$lib/app/company-context';
import {
	mobileNavigationRoutes,
	navigationSections,
	type RouteDefinition,
	type RouteDomain
} from '$lib/app/routes';
import { useSubscription } from '$lib/app/subscription-context';
import { formatKstServerTime, getServerTimeOffsetMs } from '$lib/time/server-time';
import { Badge, Button } from '$lib/ui';
import { cn } from '$lib/utils';

const iconMap: Record<string, ComponentType<{ className?: string }>> = {
	'/home': Home,
	'/dashboard': LayoutDashboard,
	'/insights/hr': Activity,
	'/agents': Bot,
	'/employees': Users,
	'/attendance': Clock,
	'/shifts': CalendarClock,
	'/field-operations': Store,
	'/partners/advisory': Handshake,
	'/customer-success': LifeBuoy,
	'/engagement': HeartHandshake,
	'/news-feed': Megaphone,
	'/employee-lifecycle': UserPlus,
	'/learning': GraduationCap,
	'/organization': GitBranch,
	'/organization/insights': Activity,
	'/performance/goals': Target,
	'/performance/reviews': ClipboardCheck,
	'/performance/one-on-ones': Users,
	'/meetings': CalendarCheck,
	'/recruiting': Users,
	'/leave': CalendarDays,
	'/expenses': Receipt,
	'/certificates': IdCard,
	'/workflows': FileText,
	'/digital-contracts': ClipboardCheck,
	'/approvals': CheckSquare,
	'/payroll': Wallet,
	'/reports': FileText,
	'/group-insurance': ShieldCheck,
	'/account/profile': IdCard,
	'/account/payslips': Banknote,
	'/account/contracts': ClipboardCheck,
	'/notifications': Bell,
	'/leave/admin': CalendarCheck,
	'/employees/number-policy': Hash,
	'/leave/policy': SlidersHorizontal,
	'/approval-lines': GitBranch,
	'/work-policies': Settings2,
	'/holidays': Calendar,
	'/security': ShieldCheck,
	'/company/settings': Building,
	'/billing': CreditCard,
	'/admin': Shield,
	'/admin/companies': Building,
	'/admin/payments': CreditCard,
	'/admin/usage': LayoutDashboard,
	'/admin/audit-logs': FileText
};

function isActive(pathname: string, route: RouteDefinition): boolean {
	if (route.pattern === '/') return pathname === '/';
	if (route.pattern === '/organization') return pathname === '/organization';
	return pathname === route.pattern || pathname.startsWith(`${route.pattern}/`);
}

function NavLink({
	route,
	compact = false,
	onNavigate
}: {
	route: RouteDefinition;
	compact?: boolean;
	onNavigate?: () => void;
}) {
	const pathname = usePathname();
	const Icon = iconMap[route.pattern] ?? FileText;
	const active = isActive(pathname, route);
	const depth = compact ? 0 : (route.navDepth ?? 0);
	return (
		<Link
			href={route.pattern}
			onClick={onNavigate}
			className={cn(
				'flex min-w-0 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
				active
					? compact
						? 'bg-chip-blue-bg text-chip-blue-fg rounded-pill'
						: 'bg-primary/10 text-primary'
					: 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
				depth > 0 && 'ml-5 border-l border-border/80 pl-3 text-[13px] font-normal',
				compact && 'flex-col gap-1 px-1 text-[11px]'
			)}
			aria-current={active ? 'page' : undefined}
		>
			<Icon className={cn('size-4 shrink-0', compact && 'size-5')} />
			<span className="truncate">{route.title}</span>
		</Link>
	);
}

function ServerClock({ enabled }: { enabled: boolean }) {
	const [offsetMs, setOffsetMs] = useState<number | null>(null);
	const [clientNowMs, setClientNowMs] = useState(() => Date.now());

	useEffect(() => {
		if (!enabled) return;
		let active = true;

		async function sync() {
			try {
				const serverTime = await api.get<ServerTimeResponse>('/system/time');
				if (!active) return;
				if (!Number.isFinite(serverTime.currentEpochMillis)) {
					setOffsetMs(null);
					return;
				}
				setOffsetMs(
					getServerTimeOffsetMs({
						serverEpochMillis: serverTime.currentEpochMillis,
						clientEpochMillis: Date.now()
					})
				);
				setClientNowMs(Date.now());
			} catch {
				if (active) setOffsetMs(null);
			}
		}

		void sync();
		const syncId = window.setInterval(() => void sync(), 5 * 60 * 1000);
		return () => {
			active = false;
			window.clearInterval(syncId);
		};
	}, [enabled]);

	useEffect(() => {
		if (!enabled) return;
		const tickId = window.setInterval(() => setClientNowMs(Date.now()), 1000);
		return () => window.clearInterval(tickId);
	}, [enabled]);

	if (!enabled) return null;

	return (
		<span className="inline-flex shrink-0 items-center rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground">
			서버 {offsetMs == null ? '--:--:--' : formatKstServerTime(clientNowMs + offsetMs)} KST
		</span>
	);
}

export function AppShell({ children }: { children: ReactNode }) {
	const auth = useAuth();
	const company = useCompany();
	const subscription = useSubscription();
	const pathname = usePathname();
	const role = auth.user?.role ?? null;
	// 요금제 출처: 회사 정보(모든 역할이 보유) 우선, 관리자는 구독 정보로 보완.
	// 아직 로딩 전이면 null → routes.ts에서 보수적으로 FREE 취급(고급 기능 숨김).
	const plan = company.company?.plan ?? subscription.subscription?.plan ?? null;
	const sections = useMemo(() => navigationSections(role, plan), [role, plan]);
	const mobileRoutes = useMemo(() => mobileNavigationRoutes(role, plan), [role, plan]);
	const activeDomain = useMemo(
		() =>
			sections.find((section) => section.routes.some((route) => isActive(pathname, route)))?.domain,
		[pathname, sections]
	);
	const [domainExpansionOverrides, setDomainExpansionOverrides] = useState<
		Partial<Record<RouteDomain, boolean>>
	>({});
	const [moreOpen, setMoreOpen] = useState(false);
	const onPrimaryTab = mobileRoutes.some((route) => isActive(pathname, route));

	useEffect(() => {
		if (
			!auth.isLoggedIn ||
			auth.isSuperAdmin ||
			!auth.user?.companyId ||
			company.company ||
			company.loading
		) {
			return;
		}
		void company.load();
	}, [auth.isLoggedIn, auth.isSuperAdmin, auth.user?.companyId, company]);

	useEffect(() => {
		if (!auth.isLoggedIn || !auth.isAdmin || subscription.subscription || subscription.loading)
			return;
		void subscription.load();
	}, [auth.isLoggedIn, auth.isAdmin, subscription]);

	useEffect(() => {
		if (!moreOpen) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [moreOpen]);

	function isDomainExpanded(domain: RouteDomain, defaultOpen: boolean): boolean {
		return activeDomain === domain || (domainExpansionOverrides[domain] ?? defaultOpen);
	}

	function toggleDomain(domain: RouteDomain, defaultOpen: boolean) {
		setDomainExpansionOverrides((current) => {
			const expanded = current[domain] ?? defaultOpen;
			return {
				...current,
				[domain]: !expanded
			};
		});
	}

	const companyName =
		company.company?.name ??
		auth.impersonation?.targetCompany.name ??
		(auth.isSuperAdmin ? '운영 콘솔' : 'AntFlow');

	return (
		<div
			className={cn(
				'min-h-screen bg-background text-foreground',
				auth.isSuperAdmin && 'dark bg-zinc-950'
			)}
		>
			{auth.isImpersonating ? (
				<div className="bg-warning/20 px-4 py-2 text-sm font-medium text-warning-foreground">
					{auth.impersonation?.targetCompany.name} 임패서네이션 중
					<Button
						className="ml-3"
						size="sm"
						variant="outline"
						onClick={() => void auth.stopImpersonation()}
					>
						복귀
					</Button>
				</div>
			) : null}
			<div className="grid min-h-screen lg:grid-cols-[17rem_1fr]">
				<aside className="hidden border-r border-border bg-card/95 lg:block">
					<div className="flex h-16 items-center justify-between border-b border-border px-5">
						<Link href={auth.isSuperAdmin ? '/admin' : '/dashboard'} className="font-semibold">
							AntFlow
						</Link>
						{auth.user ? <Badge variant="outline">{auth.user.role}</Badge> : null}
					</div>
					<div className="space-y-5 p-4">
						<div className="rounded-card border border-border bg-background/70 p-3 shadow-card">
							<p className="truncate text-sm font-semibold">{companyName}</p>
							<p className="truncate text-xs text-muted-foreground">{auth.user?.email}</p>
						</div>
						{sections.map((section) => {
							const expanded = isDomainExpanded(section.domain, section.defaultOpen);
							return (
								<nav key={section.domain} className="space-y-1" aria-label={section.label}>
									<button
										type="button"
										className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
										aria-expanded={expanded}
										onClick={() => toggleDomain(section.domain, section.defaultOpen)}
									>
										<span className="truncate">{section.label}</span>
										<ChevronDown
											className={cn(
												'size-4 shrink-0 transition-transform',
												expanded && 'rotate-180'
											)}
										/>
									</button>
									{expanded ? (
										<div className="space-y-1">
											{section.routes.map((route) => (
												<NavLink key={route.pattern} route={route} />
											))}
										</div>
									) : null}
								</nav>
							);
						})}
					</div>
				</aside>
				<div className="flex min-w-0 flex-col">
					<header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-background/95 px-4 backdrop-blur lg:h-16 lg:px-6">
						<div className="min-w-0">
							<p className="truncate text-sm font-semibold">{companyName}</p>
							<div className="flex min-w-0 items-center gap-2">
								<p className="truncate text-xs text-muted-foreground">
									{auth.user?.name ?? auth.user?.email}
								</p>
								<ServerClock enabled={auth.isLoggedIn} />
							</div>
						</div>
						<div className="flex items-center gap-2">
							<Link
								href="/notifications"
								aria-label="알림"
								className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
							>
								<Bell className="size-5" />
							</Link>
							<Button variant="ghost" size="sm" onClick={auth.logout}>
								<LogOut className="size-4" />
								로그아웃
							</Button>
						</div>
					</header>
					<main className="mx-auto min-w-0 w-full max-w-7xl flex-1 px-4 py-6 pb-24 lg:px-8">
						{children}
					</main>
				</div>
			</div>
			{moreOpen ? (
				<div
					className="fixed inset-0 z-40 lg:hidden"
					role="dialog"
					aria-modal="true"
					aria-label="전체 메뉴"
				>
					<button
						type="button"
						aria-label="메뉴 닫기"
						className="absolute inset-0 bg-foreground/40"
						onClick={() => setMoreOpen(false)}
					/>
					<div className="absolute inset-x-0 bottom-0 flex max-h-[82vh] flex-col rounded-t-2xl border-t border-border bg-card shadow-[0_-8px_30px_rgb(15_23_42/0.18)]">
						<div className="flex items-center justify-between border-b border-border px-4 py-3">
							<p className="text-sm font-semibold">전체 메뉴</p>
							<button
								type="button"
								aria-label="메뉴 닫기"
								className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
								onClick={() => setMoreOpen(false)}
							>
								<X className="size-5" />
							</button>
						</div>
						<div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
							{sections.map((section) => (
								<nav key={section.domain} className="space-y-1" aria-label={section.label}>
									<p className="px-3 text-xs font-semibold text-muted-foreground">
										{section.label}
									</p>
									{section.routes.map((route) => (
										<NavLink
											key={route.pattern}
											route={route}
											onNavigate={() => setMoreOpen(false)}
										/>
									))}
								</nav>
							))}
						</div>
					</div>
				</div>
			) : null}
			<nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/96 px-2 pb-[calc(env(safe-area-inset-bottom)+0.25rem)] pt-1 shadow-nav backdrop-blur lg:hidden">
				<div className="mx-auto grid max-w-xl grid-cols-5">
					{mobileRoutes.map((route) => (
						<NavLink key={route.pattern} route={route} compact />
					))}
					<button
						type="button"
						onClick={() => setMoreOpen((open) => !open)}
						aria-expanded={moreOpen}
						aria-label="더보기"
						className={cn(
							'flex min-w-0 flex-col items-center gap-1 rounded-md px-1 py-2 text-[11px] font-medium transition-colors',
							moreOpen || !onPrimaryTab
								? 'bg-primary/10 text-primary'
								: 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
						)}
					>
						<Menu className="size-5 shrink-0" />
						<span className="truncate">더보기</span>
					</button>
				</div>
			</nav>
		</div>
	);
}

export function useUnreadNotificationCount() {
	useEffect(() => {
		void api.get('/notifications/unread-count').catch(() => undefined);
	}, []);
}
